<template>
    <div class="ActivityHistoryTable">
        <InfiniteScroll @scroll="$emit('scroll-end')">
            <Table>
                <template #toolbarEnd>
                    <Popover
                        :open="filterPopoverOpen"
                        position-x="end"
                        :auto-flip="true"
                        outside-click-ignore-selector=".q-menu"
                        @hide="filterPopoverOpen = false"
                    >
                        <template #base>
                            <NewButton
                                class="ActivityHistoryTable__filtersButton"
                                variant="outline"
                                color="neutral"
                                @click="filterPopoverOpen = !filterPopoverOpen"
                            >
                                <EidIcon :icon-src="faFilter" />
                                Filters
                                <span
                                    v-if="activeFilterCount > 0"
                                    class="ActivityHistoryTable__filtersBadge"
                                >
                                    {{ activeFilterCount }}
                                </span>
                            </NewButton>
                        </template>
                        <ActivityHistoryFiltersPanel
                            :filters="filters"
                            :enum-labels="enumLabels"
                            :activity-types="activityTypes"
                            :show-tags="showTags"
                            :client-tags="clientTags"
                            :system-tags="systemTags"
                            :client-tags-map="clientTagsMap"
                            :system-tags-map="systemTagsMap"
                            @apply="onFiltersApply"
                            @close="filterPopoverOpen = false"
                        />
                    </Popover>
                </template>

                <template #head>
                    <HeadCell class="ActivityHistoryTable__dateHeader">
                        Date
                    </HeadCell>
                    <HeadCell class="ActivityHistoryTable__eventHeader">
                        Event
                    </HeadCell>
                    <HeadCell class="ActivityHistoryTable__detailsHeader">
                        Details
                    </HeadCell>
                    <HeadCell class="ActivityHistoryTable__sourceHeader">
                        Source
                    </HeadCell>
                    <HeadCell v-if="showTags" class="ActivityHistoryTable__tagsHeader">
                        Tag/Requirement
                    </HeadCell>
                    <HeadCell v-if="showViewColumn" class="ActivityHistoryTable__viewHeader" />
                </template>

                <ActivityHistoryTableRow
                    v-for="(entry, index) in entries"
                    :key="`${entry.activityType}-${entry.createdAt}-${index}`"
                    :entry="entry"
                    :enum-labels="enumLabels"
                    :requirement-definitions="requirementDefinitions"
                    :requirement-instance-models="requirementInstanceModels"
                    :client-tags-map="clientTagsMap"
                    :system-tags-map="systemTagsMap"
                    :active-verification-ids="activeVerificationIds"
                    :criterion-titles="criterionTitles"
                    :show-tags="showTags"
                    :show-view-column="showViewColumn"
                    @view="$emit('view', $event)"
                />

                <template v-if="entries.length === 0 && !loading && finished" #message>
                    <div class="ActivityHistoryTable__empty">
                        No activity history found.
                    </div>
                </template>
                <template v-else-if="loading" #status>
                    <LineLoader />
                </template>
            </Table>
        </InfiniteScroll>
    </div>
</template>

<style lang="scss">
    .ActivityHistoryTable {
        &__empty {
            padding: 32px;
            text-align: center;
            color: var(--eidTechGray);
        }

        &__filtersBadge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 18px;
            height: 18px;
            padding: 0 5px;
            border-radius: 9px;
            background: var(--eidTrustGreen);
            color: var(--eidWhite);
            font-size: 0.7rem;
            font-weight: 600;
            line-height: 1;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faFilter } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { InfiniteScroll } from '@evidentid/dashboard-commons/components/InfiniteScroll';
    import { LineLoader } from '@evidentid/dashboard-commons/components/LineLoader';
    import Popover from '@evidentid/dashboard-commons/components/Popover/Popover.vue';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import {
        ActivityHistoryEntry,
        ActivityHistoryQuery,
        ActivityHistorySystemTags,
        ActivityHistoryTagsMap,
        ActivityType,
        ENTITY_ACTIVITY_TYPES,
    } from '@evidentid/tprm-portal-lib/models/activity-history';
    import {
        TprmRequirementCategory,
        TprmRequirementDefinition,
    } from '@evidentid/tprm-portal-lib/models/common';
    import { TprmRequirementInstanceModel } from '@evidentid/tprm-portal-lib/models/common/TprmRequirementInstanceModel.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { ActivityHistoryFilters } from '@/modules/entity-events/models/ActivityHistoryFilters.model';

    import ActivityHistoryFiltersPanel from './sub-components/ActivityHistoryFiltersPanel.vue';
    import ActivityHistoryTableRow from './sub-components/ActivityHistoryTableRow.vue';

    export default defineComponent({
        name: 'ActivityHistoryTable',
        components: {
            ActivityHistoryFiltersPanel,
            ActivityHistoryTableRow,
            EidIcon,
            HeadCell,
            InfiniteScroll,
            LineLoader,
            NewButton,
            Popover,
            Table,
        },
        props: {
            entries: {
                type: Array as PropType<ActivityHistoryEntry[]>,
                default: (): ActivityHistoryEntry[] => [],
            },
            loading: {
                type: Boolean,
                default: false,
            },
            finished: {
                type: Boolean,
                default: false,
            },
            filters: {
                type: Object as PropType<ActivityHistoryFilters>,
                default: (): ActivityHistoryFilters => ({ entryType: [], tags: [], causedBy: '' }),
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
            clientTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            systemTags: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            requirementDefinitions: {
                type: Object as PropType<Record<TprmRequirementType, TprmRequirementDefinition>>,
                default: (): Record<TprmRequirementType, TprmRequirementDefinition> => (
                    {} as Record<TprmRequirementType, TprmRequirementDefinition>
                ),
            },
            requirementInstanceModels: {
                type: Object as PropType<Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>>,
                default: (): Record<TprmRequirementCategory, TprmRequirementInstanceModel[]> => (
                    {} as Record<TprmRequirementCategory, TprmRequirementInstanceModel[]>
                ),
            },
            clientTagsMap: {
                type: Object as PropType<ActivityHistoryTagsMap | null>,
                default: null,
            },
            systemTagsMap: {
                type: Object as PropType<ActivityHistorySystemTags | null>,
                default: null,
            },
            activeVerificationIds: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
            criterionTitles: {
                type: Object as PropType<Record<string, string>>,
                default: (): Record<string, string> => ({}) as Record<string, string>,
            },
            showTags: {
                type: Boolean,
                default: true,
            },
            showViewColumn: {
                type: Boolean,
                default: true,
            },
            activityTypes: {
                type: Array as PropType<ActivityType[]>,
                default: (): ActivityType[] => ENTITY_ACTIVITY_TYPES,
            },
        },
        // PRODUCT-26110 Task 13 follow-up: declares every event this component `$emit`s
        // (`view`/`filter-change` directly, `scroll-end` forwarded from InfiniteScroll's
        // own `scroll`) -- without a declared `emits`, Vue 3 warns "Component emitted
        // event 'scroll-end' but it is neither declared in the emits option nor as an
        // 'onScrollEnd' prop" every time it fires (seen x22 in live QA).
        emits: [ 'view', 'filter-change', 'scroll-end' ],
        data() {
            return {
                faFilter,
                filterPopoverOpen: false,
            };
        },
        computed: {
            activeFilterCount(): number {
                const { entryType, tags, causedBy } = this.filters;
                return (entryType.length ? 1 : 0) + (tags.length ? 1 : 0) + (causedBy.trim() ? 1 : 0);
            },
        },
        methods: {
            onFiltersApply(query: ActivityHistoryQuery): void {
                this.filterPopoverOpen = false;
                this.$emit('filter-change', query);
            },
        },
    });
</script>
